import React, { useCallback } from 'react'
import { Button, Text, Box, Loader } from '@vibe/core'
import { Edit, Delete, Show } from '@vibe/icons'
import { PermissionGate } from '../common/PermissionGate'
import type { JobDefinition } from '../../types'
import { PermissionResource, PermissionAction } from '../../types'

interface JobDefinitionListProps {
  jobDefinitions: JobDefinition[]
  loading: boolean
  hasMore: boolean
  onLoadMore: () => void
  onEdit: (jobDefinition: JobDefinition) => void
  onDelete: (jobDefinition: JobDefinition) => void
  onView: (jobDefinition: JobDefinition) => void
}

interface JobDefinitionRowProps {
  jobDefinition: JobDefinition
  onEdit: (jobDefinition: JobDefinition) => void
  onDelete: (jobDefinition: JobDefinition) => void
  onView: (jobDefinition: JobDefinition) => void
}

const JobDefinitionRow: React.FC<JobDefinitionRowProps> = ({
  jobDefinition,
  onEdit,
  onDelete,
  onView,
}) => {
  const formatDate = useCallback((dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch {
      return 'Fecha inválida'
    }
  }, [])

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <Box>
          <Text className="font-medium text-gray-900">
            {jobDefinition.name}
          </Text>
          <Text className="text-sm text-gray-500">{jobDefinition.puesto}</Text>
        </Box>
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <Text className="text-sm text-gray-600">
          {formatDate(jobDefinition.created_at)}
        </Text>
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <Text className="text-sm text-gray-600">v{jobDefinition.version}</Text>
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            jobDefinition.is_active
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {jobDefinition.is_active ? 'Activa' : 'Inactiva'}
        </span>
      </td>

      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <Box className="flex items-center justify-end gap-2">
          <Button
            kind="tertiary"
            size="small"
            onClick={() => onView(jobDefinition)}
            aria-label="Ver detalles"
          >
            <Show />
          </Button>

          <PermissionGate
            requiredPermission={{
              resource: PermissionResource.SOLICITUDES,
              action: PermissionAction.UPDATE,
            }}
          >
            <Button
              kind="tertiary"
              size="small"
              onClick={() => onEdit(jobDefinition)}
              aria-label="Editar definición"
            >
              <Edit />
            </Button>
          </PermissionGate>

          <PermissionGate
            requiredPermission={{
              resource: PermissionResource.SOLICITUDES,
              action: PermissionAction.DELETE,
            }}
          >
            <Button
              kind="tertiary"
              size="small"
              color="negative"
              onClick={() => onDelete(jobDefinition)}
              aria-label="Eliminar definición"
            >
              <Delete />
            </Button>
          </PermissionGate>
        </Box>
      </td>
    </tr>
  )
}

export const JobDefinitionList: React.FC<JobDefinitionListProps> = ({
  jobDefinitions = [], // Default to empty array to prevent undefined errors
  loading,
  hasMore,
  onLoadMore,
  onEdit,
  onDelete,
  onView,
}) => {
  if (loading && jobDefinitions.length === 0) {
    return (
      <Box className="flex justify-center items-center py-12">
        <Loader size="large" />
      </Box>
    )
  }

  if (!loading && jobDefinitions.length === 0) {
    return (
      <Box className="text-center py-12">
        <Box className="mb-4">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </Box>
        <Text className="text-lg font-medium text-gray-900 mb-2">
          No hay definiciones de puesto
        </Text>
        <Text className="text-gray-500 mb-6">
          Comienza creando tu primera definición de puesto para agilizar el
          proceso de reclutamiento.
        </Text>
        <PermissionGate
          requiredPermission={{
            resource: PermissionResource.SOLICITUDES,
            action: PermissionAction.CREATE,
          }}
        >
          <Button onClick={() => window.location.reload()}>
            Crear Primera Definición
          </Button>
        </PermissionGate>
      </Box>
    )
  }

  return (
    <Box>
      <Box className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto overflow-y-visible">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Definición
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creada
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Versión
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {jobDefinitions.map(jobDefinition => (
                <JobDefinitionRow
                  key={jobDefinition.id}
                  jobDefinition={jobDefinition}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onView={onView}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Box>

      {/* Load more button */}
      {hasMore && (
        <Box className="mt-6 text-center">
          <Button
            kind="tertiary"
            onClick={onLoadMore}
            disabled={loading}
            loading={loading}
          >
            Cargar más definiciones
          </Button>
        </Box>
      )}

      {/* Loading indicator for additional items */}
      {loading && jobDefinitions.length > 0 && (
        <Box className="flex justify-center items-center py-4">
          <Loader size="medium" />
        </Box>
      )}

      {/* Results summary */}
      <Box className="mt-4 text-center">
        <Text className="text-sm text-gray-500">
          Mostrando {jobDefinitions.length} definiciones
          {hasMore && ' (hay más disponibles)'}
        </Text>
      </Box>
    </Box>
  )
}

export default JobDefinitionList
